import { Component, OnInit, ViewChild, AfterViewInit, OnDestroy, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator, MatPaginatorIntl } from '@angular/material/paginator';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
// Importar la interfaz y el servicio actualizado
import { SolicitarVisitaService, PagedRequestOptions } from 'src/app/services/solicitar-visita.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { EspecialidadesService } from 'src/app/services/especialidades.service';
import { StorageService } from 'src/app/services/storage.service';
import { Subscription, Subject, merge, of as observableOf } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith, catchError } from 'rxjs/operators';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import { PaginatorIntl } from 'src/app/paginator-intl';
import { SharedModule } from '@app/shared/shared.module';
import { TipoServicioService } from '@app/services/tipo-servicio.service';
import { ClientesService } from 'src/app/services/clientes.service';

@Component({
  selector: 'app-solicitudes-rechazadas',
  standalone: true,
  imports: [
    CommonModule, MatTableModule, MatPaginatorModule, MatSortModule, MatButtonModule,
    MatIconModule, MatTooltipModule, MatCardModule, MatFormFieldModule, MatInputModule,
    RouterModule, MatProgressSpinnerModule,
    MatSelectModule, SharedModule
  ],
  templateUrl: './solicitudes-rechazadas.component.html', // Usar archivo HTML externo
  styleUrls: ['./solicitudes-rechazadas.component.scss'], // Usar archivo SCSS externo
  providers: [
    { provide: MatPaginatorIntl, useClass: PaginatorIntl }
  ]
})
export class SolicitudesRechazadasComponent implements OnInit, AfterViewInit, OnDestroy {
  baseDisplayedColumns: string[] = [
    'id', 
    'fechaIngreso',  
    'local', 
    'zona',
    'especialidad',    
    'ticketGruman',     
    'observaciones', 
    'generado_por', 
    'tipoServicio', 
    'tipo_solicitud',
    'rechazado_por', 
    'motivo_rechazo'
  ];
  displayedColumns: string[] = [...this.baseDisplayedColumns]; // Array dinámico
  dataSource = new MatTableDataSource<any>();
  loading = true;
  totalItems = 0;
   selectedServiceType: string = '';
  tiposServicio: any[] = [];

  selectedCompany: any = null;
  public isGruman: boolean = false;
  public clientes: any[] = [];
  public selectedClientId: number | null = null;
  private companySubscription: Subscription | null = null;
  private searchSubject = new Subject<string>();

  meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  anios: number[] = [];
  selectedMes: string = '';
  selectedAnio: string = '';

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('input') input!: ElementRef;

  especialidades: { [key: number]: string } = {};

  constructor(
    private solicitarVisitaService: SolicitarVisitaService,
    private especialidadesService: EspecialidadesService,
    private tipoServicioService: TipoServicioService,
    private clientesService: ClientesService,
    private router: Router,
    private route: ActivatedRoute,
    private storage: StorageService
  ) {}

  ngOnInit() {
    this.generarAnios();
    this.loadEspecialidades();
    this.getTiposServicio();

    // Capturar si venimos desde el Dashboard con un mes específico
    this.route.queryParams.subscribe((params: any) => {
      if (params['mesFacturacion']) {
        const parts = params['mesFacturacion'].split(' ');
        if (parts.length === 2) {
          this.selectedMes = parts[0];
          this.selectedAnio = parts[1];
        }
      }
    });
    
    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(() => {
      this.paginator.pageIndex = 0;
      this.loadSolicitudes();
    });

    this.companySubscription = this.storage.companyChange$.subscribe((company: any) => {
      this.selectedCompany = company;

      this.isGruman = this.selectedCompany?.nombre?.trim().toUpperCase() === 'GRUMAN';
      this.selectedClientId = null;
      if (this.isGruman) {
        this.loadClientes();
      }

      this.updateDisplayedColumns(this.selectedCompany);

      if(this.paginator) {
        this.paginator.pageIndex = 0;
        this.loadSolicitudes();
      }
    });

    const user = this.storage.getItem('currentUser');
    if (user && user.selectedCompany) {      
      this.selectedCompany = user.selectedCompany;
      // Inicializar el filtro de cliente en la carga si es GRUMAN
      this.isGruman = this.selectedCompany.nombre?.trim().toUpperCase() === 'GRUMAN';
      this.updateDisplayedColumns(this.selectedCompany);
      if (this.isGruman) {
        this.loadClientes();
      }
    } else {      
      this.updateDisplayedColumns(null);
    }
  }
  
  // Cargar catálogo de clientes para el dropdown
  loadClientes(): void {
    this.clientesService.getClientesWithGruman().subscribe({
      next: (clientes: any[]) => this.clientes = clientes || [],
      error: (err: any) => console.error('Error al cargar clientes:', err)
    });
  }

  // Evento al seleccionar un cliente en la vista GRUMAN
  onClientFilterChange(): void {
    if (this.paginator) this.paginator.pageIndex = 0;
    this.loadSolicitudes();
  }

  ngAfterViewInit() {
    merge(this.sort.sortChange, this.paginator.page)
      .pipe(startWith({}))
      .subscribe(() => this.loadSolicitudes());
  }
  
  ngOnDestroy() {
    if (this.companySubscription) {
      this.companySubscription.unsubscribe();
    }
  }

  generarAnios(): void {
    const currentYear = new Date().getFullYear();
    for (let y = 2023; y <= currentYear + 2; y++) {
      this.anios.push(y);
    }
  }

  onDateFilterChange() {
    if (this.paginator) this.paginator.pageIndex = 0;
    this.loadSolicitudes();
  }

  getTiposServicio(): void {
    this.tipoServicioService.findAll().subscribe({
      next: (data: any) => {
        this.tiposServicio = data;
      },
      error: (err: any) => console.error('Error cargando tipos de servicio', err)
    });
  }

  private updateDisplayedColumns(company: any) {
    // Comienza con las columnas base
    const newCols = [...this.baseDisplayedColumns];
    let esGruman = false;

    // Verificación robusta contra espacios o mayúsculas
    if (company && company.nombre) {
      const nombreLimpio = company.nombre.trim().toUpperCase();
      if (nombreLimpio === 'GRUMAN') {
        esGruman = true;
      }
    }

    // Añade la columna 'acciones' solo si es Gruman
    if (esGruman) {
      newCols.push('acciones');
      newCols.splice(2, 0, 'cliente');
    }

    // Asigna el nuevo array de columnas
    this.displayedColumns = newCols;
  }

  loadSolicitudes() {
    this.loading = true;

    let mesFacturacionStr = '';
    if (this.selectedMes) mesFacturacionStr += this.selectedMes;
    if (this.selectedAnio) mesFacturacionStr += (mesFacturacionStr ? ' ' : '') + this.selectedAnio;

    const options: PagedRequestOptions = {
      page: this.paginator.pageIndex + 1,
      limit: this.paginator.pageSize,
      searchTerm: this.input.nativeElement.value,
      serviceType: this.selectedServiceType,
      sortBy: this.sort.active || 'id',
      sortOrder: this.sort.direction || 'desc',
      mesFacturacion: mesFacturacionStr || undefined
    };

    if (this.isGruman) {
      if (this.selectedClientId) {
        options.clientId = this.selectedClientId;
      }
    } else if (this.selectedCompany?.id) {
      options.clientId = this.selectedCompany.id;
    }

    this.solicitarVisitaService.getSolicitudesRechazadasPaginado(options)
    .pipe(
      catchError(() => observableOf(null))
    ).subscribe({
      next: (response: any) => {
        this.dataSource.data = response ? response.data : [];
        this.totalItems = response ? response.total : 0;
        this.loading = false;
      },
      error: (error: any) => {
        console.error('Error cargando solicitudes rechazadas:', error);
        this.loading = false;
      }
    });
  }
  
  loadEspecialidades() {
    this.especialidadesService.findAll().subscribe({
      next: (especialidades: any) => {
        this.especialidades = especialidades.reduce((acc: any, esp: any) => {
          acc[esp.id] = esp.nombre;
          return acc;
        }, {} as { [key: number]: string });
      }
    });
  }

  applyFilter() {
    this.searchSubject.next(this.input.nativeElement.value);
  }

  onServiceTypeChange() {
    this.paginator.pageIndex = 0; // Volvemos a la página 1
    this.loadSolicitudes();       // Recargamos la tabla
  }

  resetFilters() {
    this.input.nativeElement.value = '';
    this.selectedServiceType = '';
    this.selectedClientId = null;
    this.paginator.pageIndex = 0;
    this.sort.active = 'id';
    this.sort.direction = 'desc';
    this.selectedMes = '';
    this.selectedAnio = '';
    this.getTiposServicio();
    this.loadSolicitudes();
  }

  canViewDetail(row: any): boolean {
    if (row.facturacion && (row.facturacion.is_cerrada === true || row.facturacion.is_cerrada === 1)) {
      return false;
    }
    return true;
  }

  verDetalle(id: number) {
    this.router.navigate(['transacciones/solicitudes-de-visita/ver-solicitud/rechazada', id]);
  }

  exportarExcel() {
    Swal.fire({
      title: 'Generando exportación...',
      text: 'Esto puede tardar unos segundos, por favor espera.',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading()
    });

    let mesFacturacionStr = '';
    if (this.selectedMes) mesFacturacionStr += this.selectedMes;
    if (this.selectedAnio) mesFacturacionStr += (mesFacturacionStr ? ' ' : '') + this.selectedAnio;

    // 1. Construir las opciones (Inyectamos el status directamente por seguridad)
    const options: PagedRequestOptions | any = {
      export: true,
      searchTerm: this.input.nativeElement.value,
      serviceType: this.selectedServiceType,
      sortBy: this.sort.active || 'id',
      sortOrder: this.sort.direction || 'desc',
      mesFacturacion: mesFacturacionStr || undefined,
      status: 'rechazada'
    };

    if (this.isGruman) {
      if (this.selectedClientId) {
        options.clientId = this.selectedClientId;
      }
    } else if (this.selectedCompany?.id) {
      options.clientId = this.selectedCompany.id;
    }

    // 2. Llamada al servicio
    this.solicitarVisitaService.descargarExcelSolicitudes(options).subscribe({
      next: (response: any) => {
        if (!response || !response.data || response.data.length === 0) {
          Swal.fire('Sin datos', 'No hay solicitudes rechazadas para exportar.', 'warning');
          return;
        }

        // 3. Delegar la generación del Excel al servicio maestro
        this.solicitarVisitaService.exportarExcelMaestro(response.data, 'Rechazadas');
      },
      error: (err: any) => {
        console.error('Error al exportar:', err);
        Swal.close();
        Swal.fire('Error', 'Hubo un problema al generar el Excel.', 'error');
      }
    });
  }
}